import React, { useEffect, useMemo, useRef, useState } from "react";
import InforIcon from "../../assets/InfoCircleIcon";
import styles from "./LimitsUsageBanner.module.css";
import {
  QueryTypeUsage,
  TableUsage,
  SelectedTableRef,
  StatusTone,
  tableUsageMatchesSelection,
  isNearLimit,
  isLimitReached,
  tableStatus,
} from "../../hooks/use-limits-usage";
import { OI } from "../../onam/mark";
import { FEATURES } from "../../onam/features";

type Props = {
  loading?: boolean;
  usage?: QueryTypeUsage;
  // Narrows table_usage down to just these org+table pairs (case-insensitive
  // match on both) - e.g. the datasets a query is actually being built
  // against. Table name alone isn't enough to identify a selected dataset:
  // the same physical_table_name can be contributed by more than one org in
  // a federation, so matching on table name only pulls in an unrelated org's
  // usage row alongside the one actually selected. Omit to show every
  // table_usage entry for the objective.
  selectedTables?: SelectedTableRef[];
  // Drops the standalone-banner vertical margins so this can sit flush
  // next to other inline elements, e.g. a section header title.
  inline?: boolean;
};

// Two different contributor orgs can have a bucket backed by the same
// physical_table_name, so org+table (not table name alone) is required to
// disambiguate table_usage rows.
function tableKey(t: TableUsage): string {
  return `${t.organization_id}-${t.physical_table_name}`;
}

const STATUS_CLASS: Record<StatusTone, string> = {
  ok: styles.statusOk,
  warning: styles.statusWarning,
  reached: styles.statusReached,
  disabled: styles.statusDisabled,
  inactive: styles.statusInactive,
};

// LimitsUsageBanner is a compact, non-blocking, informational read of
// GET /query/limits-usage - it never disables submission or duplicates the
// backend's enforcement (enforceTableQueryLimits / enforceOrgQueryTypeLimit
// already do that and surface their own rejection message on submit); it
// only gives the user visibility into remaining quota before they act.
// Table-level detail only ever appears in the popup, never inline, to keep
// the page uncluttered - the popup opens on click, or automatically (once
// per table) the moment a table's query limit is actually reached.
const LimitsUsageBanner: React.FC<Props> = ({
  loading,
  usage,
  selectedTables,
  inline,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const alertedKeysRef = useRef<Set<string>>(new Set());

  const relevantTables = useMemo(() => {
    if (!usage) return undefined;
    return selectedTables?.length
      ? usage.table_usage?.filter((t) =>
          tableUsageMatchesSelection(t, selectedTables),
        )
      : usage.table_usage;
  }, [usage, selectedTables]);

  const reachedTables = useMemo(
    () => relevantTables?.filter(isLimitReached) ?? [],
    [relevantTables],
  );
  const reachedKeysSignature = reachedTables.map(tableKey).join("|");

  useEffect(() => {
    const unseen = reachedTables.filter(
      (t) => !alertedKeysRef.current.has(tableKey(t)),
    );
    if (unseen.length === 0) return;
    unseen.forEach((t) => alertedKeysRef.current.add(tableKey(t)));
    setIsOpen(true);
    // reachedKeysSignature is the stable proxy for reachedTables' identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reachedKeysSignature]);

  if (loading || !usage || !relevantTables?.length) {
    return null;
  }

  const describeTable = (t: TableUsage) => {
    const orgName = t.organization_name || t.organization_id;
    const base = `Org: ${orgName}${t.dataset_name ? `, Dataset: ${t.dataset_name}` : ""}`;
    return t.is_live ? base : `${base} — dataset no longer active`;
  };

  const hasNearLimit = relevantTables.some(
    (t) => isNearLimit(t) && !isLimitReached(t),
  );
  const worstStatus: StatusTone | null = reachedTables.length
    ? "reached"
    : hasNearLimit
      ? "warning"
      : null;

  return (
    <div className={inline ? `${styles.banner} ${styles.bannerInline}` : styles.banner}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setIsOpen(true)}
      >
        <InforIcon />
        <span className={styles.triggerLabel}>Table query limits</span>
      </button>
      {worstStatus && (
        <span className={`${styles.pill} ${STATUS_CLASS[worstStatus]}`}>
          {worstStatus === "reached" ? "Limit reached" : "Nearing limit"}
        </span>
      )}

      {isOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Table Query Limits</h3>
              <button
                className={styles.closeBtn}
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                {(FEATURES.curationSkin ? <OI n="close" size="1em" /> : "✕")}
              </button>
            </div>
            <div className={styles.modalBody}>
              {reachedTables.length > 0 && (
                <div className={styles.reachedBanner}>
                  <strong>Query limit reached</strong>
                  <ul className={styles.reachedList}>
                    {reachedTables.map((t) => {
                      const description = describeTable(t);
                      return (
                        <li key={tableKey(t)}>
                          {t.physical_table_name}
                          {description && (
                            <span className={styles.limitDetail}>
                              {" "}
                              ({description})
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  <p className={styles.reachedHint}>
                    Contact your administrator to raise these limits.
                  </p>
                </div>
              )}

              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Table</th>
                    <th>Usage</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {relevantTables.map((t) => {
                    const status = tableStatus(t);
                    const description = describeTable(t);
                    return (
                      <tr key={tableKey(t)}>
                        <td>
                          {t.physical_table_name}
                          {description && (
                            <div className={styles.limitDetail}>
                              {description}
                            </div>
                          )}
                        </td>
                        <td>
                          {t.is_live && t.enabled_flag
                            ? `${t.query_count}/${t.query_limit} (${t.query_left} left)`
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${STATUS_CLASS[status.tone]}`}
                          >
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LimitsUsageBanner;
